import type { Food, MacroKey, Meal, MealOption, Pct, Plan, StatusInfo, Targets } from '../types';

export const TOL = 0.2;          // ±20 %
export const MIN_G = 3;          // never tighter than ±3 g on macros
export const KCAL_PER: Record<MacroKey, number> = { p: 4, c: 4, f: 9 };
export const MACRO_LABEL: Record<MacroKey, string> = { p: 'protein', c: 'carbs', f: 'fats' };
const IDX: Record<MacroKey, 1 | 2 | 3> = { p: 1, c: 2, f: 3 };

export const totals = (foods: Food[]): Targets =>
  foods.reduce((a, f) => ({ k: a.k + f.per[0] * f.qty, p: a.p + f.per[1] * f.qty, c: a.c + f.per[2] * f.qty, f: a.f + f.per[3] * f.qty }), { k: 0, p: 0, c: 0, f: 0 });

/** Target for a meal: kcal × share × ratio; gram overrides win. */
export const mealTargets = (plan: Pick<Plan, 'kcal' | 'pct'>, m: Meal): Targets => {
  const k = m.skip ? 0 : plan.kcal * ((+m.share || 0) / 100);
  const ra = m.ratio ?? plan.pct;
  const g = (key: MacroKey) => m.ov[key] ?? (k * ra[key]) / 100 / KCAL_PER[key];
  return { k, p: g('p'), c: g('c'), f: g('f') };
};

export type Check = 'none' | 'low' | 'high' | 'ok';
export const check = (actual: number, target: number, grams: boolean): Check => {
  if (!(target > 0)) return 'none';
  const band = grams ? Math.max(target * TOL, MIN_G) : target * TOL;
  return actual < target - band ? 'low' : actual > target + band ? 'high' : 'ok';
};

export type MacroDrift = { key: MacroKey; actual: number; goal: number };

/** Meal-target footer: macros outside ±20% (min ±3 g band) vs daily gram goals. */
export const macroTotalsDrift = (
  totals: { p: number; c: number; f: number },
  goals: { p: number; c: number; f: number },
): MacroDrift[] =>
  (['p', 'c', 'f'] as MacroKey[]).flatMap(key => {
    const goal = goals[key];
    const actual = totals[key];
    const c = check(actual, goal, true);
    const belowMin = goal > 0 && actual < MIN_G;
    if (c === 'low' || c === 'high' || belowMin) return [{ key, actual, goal }];
    return [];
  });

export const evaluate = (foods: Food[], T: Targets) => {
  const A = totals(foods);
  const issues: string[] = [];
  (['k', 'p', 'c', 'f'] as const).forEach(key => {
    const c = check(A[key], T[key], key !== 'k');
    if (c === 'low' || c === 'high') issues.push(`${c} ${key === 'k' ? 'kcal' : MACRO_LABEL[key]}`);
  });
  return { A, issues };
};

export const status = (foods: Food[], T: Targets): StatusInfo => {
  if (!(T.k > 0)) return { st: 'ok', label: 'No target', issues: [] };
  if (!foods.length) return { st: 'empty', label: 'Empty', issues: [] };
  const { issues } = evaluate(foods, T);
  if (!issues.length) return { st: 'ok', label: 'On target', issues };
  const first = issues[0][0].toUpperCase() + issues[0].slice(1);
  return { st: issues.length > 1 ? 'bad' : 'warn', label: first + (issues.length > 1 ? ` +${issues.length - 1}` : ''), issues };
};

const mean = (a: number[]) => (a.length ? a.reduce((s, n) => s + n, 0) / a.length : 0);

/** Average kcal + macro ratio across the non-empty options of a meal (web v4 "Set from options"). */
export const ratioFromOptions = (options: MealOption[]): (Pct & { k: number }) | null => {
  const T = options.filter(o => o.foods.length).map(o => totals(o.foods));
  if (!T.length) return null;
  const mp = mean(T.map(t => t.p)), mc = mean(T.map(t => t.c)), mf = mean(T.map(t => t.f));
  const e = 4 * mp + 4 * mc + 9 * mf;
  if (!e) return null;
  return { p: (4 * mp) / e * 100, c: (4 * mc) / e * 100, f: (9 * mf) / e * 100, k: mean(T.map(t => t.k)) };
};

/** Shares (1 decimal) proportional to kcal, nudged so they sum to exactly 100. */
export const sharesFromKcal = (ks: number[]): number[] => {
  const sum = ks.reduce((a, b) => a + b, 0);
  if (!sum) return ks.map(() => 0);
  const sh = ks.map(k => Math.round((k / sum) * 1000) / 10);
  const i = sh.indexOf(Math.max(...sh));
  sh[i] = Math.round((sh[i] + 100 - sh.reduce((a, b) => a + b, 0)) * 10) / 10;
  return sh;
};

export const KCAL_MIN = 800;
export const KCAL_MAX = 4000;
export const clampKcal = (kcal: number) => Math.max(KCAL_MIN, Math.min(KCAL_MAX, kcal));

export const gramsPerDay = (kcal: number, pct: Pct, key: MacroKey) => Math.round((kcal * pct[key]) / 100 / KCAL_PER[key]);

/** Rebalance pct when one macro changes: the "other" of P/C absorbs the delta; F takes the remainder if needed. */
export const rebalancePct = (pct: Pct, key: MacroKey, value: number): Pct => {
  const nv = Math.max(5, Math.min(70, Math.round(value) || 0));
  const o: MacroKey = key === 'c' ? 'p' : 'c', th: MacroKey = key === 'f' ? 'p' : 'f';
  const next = { ...pct, [key]: nv } as Pct;
  next[o] = Math.max(5, 100 - nv - next[th]);
  next[th] = 100 - nv - next[o];
  return next;
};

export const idx = IDX;
